import React, { useState, useMemo } from 'react';
import { PasswordStrengthMeter } from '../common/PasswordStrengthMeter';
import { Button } from '../common/Button';
import { changePassword } from '../../api/users';
import { useLanguage } from '../../i18n/useLanguage';

const PASSWORD_RULES = [
  { test: (pw: string) => pw.length >= 8 },
  { test: (pw: string) => /[A-Z]/.test(pw) },
  { test: (pw: string) => /[a-z]/.test(pw) },
  { test: (pw: string) => /[0-9]/.test(pw) },
  { test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
];

export const ChangePassword: React.FC = () => {
  const { t } = useLanguage();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const allRulesPassed = useMemo(
    () => newPassword.length > 0 && PASSWORD_RULES.every((r) => r.test(newPassword)),
    [newPassword],
  );

  const validate = (): string | null => {
    if (!allRulesPassed) return t('password.tooWeak');
    if (newPassword !== confirmPassword) return t('password.mismatch');
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await changePassword(newPassword);
      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-section change-password-section">
      <h2>{t('password.changeTitle')}</h2>
      <p className="change-password-hint">{t('password.requirements')}</p>
      <form onSubmit={handleSubmit} className="change-password-form">
        <div className="form-group">
          <label className="form-label" htmlFor="new-password">{t('password.newPassword')}</label>
          <div className="password-input-wrapper">
            <input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              className="form-input"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(null); setSuccess(false); }}
              placeholder={t('password.changePlaceholder')}
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword((s) => !s)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide' : 'Show'}
            >
              {showPassword ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
          <PasswordStrengthMeter password={newPassword} />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="confirm-password">{t('password.confirmPassword')}</label>
          <input
            id="confirm-password"
            type={showPassword ? 'text' : 'password'}
            className="form-input"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setError(null); setSuccess(false); }}
            placeholder={t('password.confirmPlaceholder')}
            autoComplete="new-password"
          />
        </div>

        {error && <div className="change-password-error">{error}</div>}
        {success && <div className="change-password-success">{t('password.success')}</div>}

        <Button
          type="submit"
          variant="primary"
          size="small"
          isLoading={loading}
          disabled={loading || !allRulesPassed || !confirmPassword || newPassword !== confirmPassword}
        >
          {loading ? t('password.changing') : t('password.changeButton')}
        </Button>
      </form>
    </div>
  );
};
